import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import SheetPreview from 'components/common/SheetPreview';
import {
  updateEnrichmentMapping,
  setEnrichmentFile,
  setEnrichmentData,
  decrementEnrichmentWizardStep,
  setEnrichmentError,
} from 'actions/enrichmentWizard';
import {
  getEnrichmentData,
  getEnrichmentMappings,
  getEnrichmentSettings,
  getEnrichmentFile,
  getEnrichmentSheetName,
  getEnrichmentType,
  getEnrichmentStatus,
  getEnrichmentError,
} from 'selectors/enrichmentWizard';
import { fieldConfigs } from './validationConfig';
import Icon from 'components/common/Icon';
import {
  CloseButton,
  DownloadReportContainer,
  ErrorBox,
  ErrorBoxSpace,
  FileHeader,
  FileInfo,
  HeaderText,
  StatBox,
  StatLabel,
  StatsContainer,
  StatValue,
} from './styled';
import XLSX from 'xlsx';
import { Button } from 'antd';
import { fileExtensionToMimeType } from 'utils/file';

const MappingArea = styled.div`
  width: 90%;
  flex: 1;
`;

const MappingContainer = styled.div`
  width: 100%;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ff4444;
  display: flex;
  align-items: center;
  padding: 4px;

  &:hover {
    color: #cc0000;
  }
`;

const MappingSection = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

// New mapping interface
const MappingStep = () => {
  const dispatch = useDispatch();
  const data = useSelector(getEnrichmentData);
  const mappings = useSelector(getEnrichmentMappings);
  const settings = useSelector(getEnrichmentSettings);
  const file = useSelector(getEnrichmentFile);
  const sheetName = useSelector(getEnrichmentSheetName);
  const type = useSelector(getEnrichmentType);
  const enrichmentStatus = useSelector(getEnrichmentStatus);
  const error = useSelector(getEnrichmentError);

  const handleUnsetError = () => {
    dispatch(setEnrichmentError(null));
  };

  useEffect(() => {
    dispatch(setEnrichmentError(null));

    return () => {
      dispatch(setEnrichmentError(null));
    };
  }, [dispatch]);

  const isLoading = enrichmentStatus === 'loading';

  const stats = mappings?.[type]?.validation;

  const requiredMappings = Object.entries(fieldConfigs)
    .filter(([_, config]) => config.type === type && config.required)
    .map(([id, config]) => ({
      id,
      label: config.label,
    }));

  const handleMappingChange = (mappingId, column) => {
    dispatch(updateEnrichmentMapping(mappingId, column, true));
  };

  const handleRemoveFile = () => {
    dispatch(setEnrichmentFile(null));
    dispatch(setEnrichmentData([]));
    dispatch(decrementEnrichmentWizardStep());
  };

  const handleDownloadErrors = async () => {
    if (!stats?.invalid?.length) return;

    const xlsx = await XLSX;
    const wb = xlsx.utils.book_new();

    // Create headers for the error report
    const headers = ['Row Number', 'Value', 'Error Reason'];
    const errorData = [
      headers,
      ...stats.invalid.map(error => [
        error.rowNumber,
        error.value,
        error.reason,
      ]),
    ];

    const ws = xlsx.utils.aoa_to_sheet(errorData);
    xlsx.utils.book_append_sheet(wb, ws, 'Validation Errors');

    const excelBuffer = xlsx.write(wb, { bookType: 'csv', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'text/csv',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'validation_errors.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <MappingArea>
      <ErrorBoxSpace hasError={!!error}>
        {error && (
          <ErrorBox>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon='error' size='sm' color='red' />
              <div>{error}</div>
            </div>
            <CloseButton onClick={handleUnsetError}>
              <Icon icon='close' size='lg' color='grey' />
            </CloseButton>
          </ErrorBox>
        )}
      </ErrorBoxSpace>
      <HeaderText>Uploaded file:</HeaderText>
      <FileHeader>
        <FileInfo>
          <Icon icon='fileExcel' size='sm' color='green' />
          <span>{file?.name}</span>
          {file?.type === fileExtensionToMimeType['.xlsx'] && sheetName && (
            <span>({sheetName})</span>
          )}
        </FileInfo>
        {!isLoading && (
          <RemoveButton onClick={handleRemoveFile} title='Remove file'>
            <Icon icon='trash' size='sm' color='grey' />
          </RemoveButton>
        )}
      </FileHeader>
      <MappingContainer>
        <div>
          <HeaderText>Map your columns to the required fields</HeaderText>
          <SheetPreview
            data={data}
            mappings={mappings}
            settings={settings}
            requiredMappings={requiredMappings}
            onMappingChange={handleMappingChange}
            disabled={isLoading}
          />
        </div>
      </MappingContainer>
      {stats && (
        <MappingSection>
          <HeaderText>{`${
            type === 'organisation' ? 'Organisation' : 'Email'
          } enrichment file summary`}</HeaderText>
          <StatsContainer>
            <StatBox>
              <StatValue>{stats.total}</StatValue>
              <StatLabel>Total Rows</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>
                {stats.total -
                  (stats.valid?.length || 0) -
                  (stats.invalid?.length || 0)}
              </StatValue>
              <StatLabel>Empty Rows</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{stats.valid?.length}</StatValue>
              <StatLabel>Valid Rows</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{stats.invalid?.length}</StatValue>
              <StatLabel>Invalid Rows</StatLabel>
            </StatBox>
          </StatsContainer>
          {stats.invalid?.length > 0 && (
            <DownloadReportContainer>
              <div>Generate an error report to identify your invalid rows</div>
              <Button type='secondary' onClick={handleDownloadErrors}>
                <Icon icon='download' size='sm' color='inherit' />
                <span style={{ marginLeft: 8 }}>Download Error Report</span>
              </Button>
            </DownloadReportContainer>
          )}
        </MappingSection>
      )}
    </MappingArea>
  );
};

export default MappingStep;
